import { useState, useMemo, useCallback } from 'react'
import { useTheme } from '../context/ThemeContext'
import {
    ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, ReferenceLine, ReferenceArea
} from 'recharts'

const RANGES = [
    { label: '3M', days: 90 },
    { label: '6M', days: 180 },
    { label: '1Y', days: 365 },
    { label: '2Y', days: 730 },
    { label: 'All', days: 0 }
]

function WellAnalysisChart({ actualData = [], forecastData = [], workovers = [] }) {
    const { theme } = useTheme()
    const isDark = theme === 'dark'

    const [activeRange, setActiveRange] = useState('6M')
    const [refAreaLeft, setRefAreaLeft] = useState(null)
    const [refAreaRight, setRefAreaRight] = useState(null)
    const [zoomLeft, setZoomLeft] = useState(null)
    const [zoomRight, setZoomRight] = useState(null)
    const [isDragging, setIsDragging] = useState(false)
    const [showWorkovers, setShowWorkovers] = useState(true)
    const [showOilCut, setShowOilCut] = useState(false)

    // Build a lookup of workovers by date for tooltip
    const workoversByDate = useMemo(() => {
        const map = {}
        workovers.forEach(wo => {
            if (!map[wo.startDate]) map[wo.startDate] = []
            map[wo.startDate].push(wo)
        })
        return map
    }, [workovers])

    // Merge actual + forecast into single dataset keyed by date
    const chartData = useMemo(() => {
        const dateMap = {}

        actualData.forEach(d => {
            const oil = d.oil_bbl || 0
            const water = d.water_bbl || 0
            const totalFluid = oil + water
            dateMap[d.date] = {
                date: d.date,
                actual_oil: oil,
                actual_gas: d.gas_mcf || 0,
                actual_water: water,
                oil_cut: totalFluid > 0 ? Math.round((oil / totalFluid) * 1000) / 10 : null
            }
        })

        forecastData.forEach(d => {
            if (!dateMap[d.date]) {
                dateMap[d.date] = { date: d.date }
            }
            dateMap[d.date].forecast_oil = d.oil_bbl || 0
            dateMap[d.date].forecast_gas = d.gas_mcf || 0
            dateMap[d.date].forecast_water = d.water_bbl || 0
        })

        return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date))
    }, [actualData, forecastData])

    // Filter by range buttons first, then by drag-zoom
    const displayData = useMemo(() => {
        let data = chartData

        // Apply range button filter
        if (activeRange !== 'All') {
            const range = RANGES.find(r => r.label === activeRange)
            if (range) {
                const cutoff = new Date()
                cutoff.setDate(cutoff.getDate() - range.days)
                const cutoffStr = cutoff.toISOString().slice(0, 10)
                data = data.filter(d => d.date >= cutoffStr)
            }
        }

        // Apply drag-zoom on top
        if (zoomLeft && zoomRight) {
            data = data.filter(d => d.date >= zoomLeft && d.date <= zoomRight)
        }

        return data
    }, [chartData, activeRange, zoomLeft, zoomRight])

    // Filter workovers to visible date range
    const visibleWorkovers = useMemo(() => {
        if (!displayData.length) return []
        const minDate = displayData[0].date
        const maxDate = displayData[displayData.length - 1].date
        return workovers.filter(wo => wo.startDate >= minDate && wo.startDate <= maxDate)
    }, [workovers, displayData])

    const isZoomed = zoomLeft && zoomRight

    const handleMouseDown = useCallback((e) => {
        if (e?.activeLabel) {
            setRefAreaLeft(e.activeLabel)
            setRefAreaRight(null)
            setIsDragging(true)
        }
    }, [])

    const handleMouseMove = useCallback((e) => {
        if (isDragging && e?.activeLabel) {
            setRefAreaRight(e.activeLabel)
        }
    }, [isDragging])

    const handleMouseUp = useCallback(() => {
        if (!isDragging) return
        setIsDragging(false)

        if (refAreaLeft && refAreaRight && refAreaLeft !== refAreaRight) {
            const [left, right] = [refAreaLeft, refAreaRight].sort()
            setZoomLeft(left)
            setZoomRight(right)
        }
        setRefAreaLeft(null)
        setRefAreaRight(null)
    }, [isDragging, refAreaLeft, refAreaRight])

    const resetZoom = () => {
        setZoomLeft(null)
        setZoomRight(null)
    }

    const handleRangeClick = (label) => {
        setActiveRange(label)
        setZoomLeft(null)
        setZoomRight(null)
    }

    if (chartData.length === 0) {
        return (
            <div className={`flex items-center justify-center h-64 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                No production data available
            </div>
        )
    }

    const formatDate = (d) => {
        const dt = new Date(d + 'T00:00:00')
        return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }

    const formatDateFull = (d) => {
        const dt = new Date(d + 'T00:00:00')
        return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
    }

    const tooltipStyle = {
        backgroundColor: isDark ? '#1e293b' : '#ffffff',
        border: `1px solid ${isDark ? '#475569' : '#d1d5db'}`,
        borderRadius: '8px',
        color: isDark ? '#e2e8f0' : '#1f2937'
    }

    // Custom tooltip that includes workover info
    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload || !label) return null
        const wos = workoversByDate[label]
        return (
            <div style={tooltipStyle} className="px-3 py-2 text-xs shadow-lg">
                <p className="font-semibold mb-1">{formatDateFull(label)}</p>
                {payload.filter(p => p.value != null && p.name !== ' ').map((p, i) => (
                    <p key={i} style={{ color: p.color }}>
                        {p.name}: {p.name === 'Oil Cut %' ? `${p.value}%` : (p.value?.toLocaleString() || '--')}
                    </p>
                ))}
                {wos && wos.length > 0 && (
                    <div className="mt-1.5 pt-1.5 border-t" style={{ borderColor: isDark ? '#475569' : '#e5e7eb' }}>
                        <p className="font-semibold text-amber-500 mb-0.5">Workover{wos.length > 1 ? 's' : ''}</p>
                        {wos.map((wo, i) => (
                            <p key={i} className="text-amber-400">
                                {wo.wellName}: {wo.jobType}
                            </p>
                        ))}
                    </div>
                )}
            </div>
        )
    }

    return (
        <div>
            {/* Controls row */}
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-1.5">
                    {RANGES.map(r => (
                        <button
                            key={r.label}
                            onClick={() => handleRangeClick(r.label)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                                activeRange === r.label && !isZoomed
                                    ? 'bg-blue-600 text-white'
                                    : isDark
                                        ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            {r.label}
                        </button>
                    ))}
                    {workovers.length > 0 && (
                        <button
                            onClick={() => setShowWorkovers(!showWorkovers)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ml-2 ${
                                showWorkovers
                                    ? 'bg-amber-500 text-white'
                                    : isDark
                                        ? 'bg-slate-700 text-gray-400 hover:bg-slate-600'
                                        : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                            }`}
                        >
                            WO
                        </button>
                    )}
                    <button
                        onClick={() => setShowOilCut(!showOilCut)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                            showOilCut
                                ? 'bg-purple-600 text-white'
                                : isDark
                                    ? 'bg-slate-700 text-gray-400 hover:bg-slate-600'
                                    : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                        }`}
                    >
                        Oil Cut %
                    </button>
                </div>
                <div className="flex items-center gap-2">
                    {isZoomed && (
                        <button
                            onClick={resetZoom}
                            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                        >
                            Reset Zoom
                        </button>
                    )}
                    <span className={`text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        Drag to zoom
                    </span>
                </div>
            </div>

            <div className="h-80 sm:h-[28rem]" style={{ touchAction: 'none' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={displayData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e5e7eb'} />
                        <XAxis
                            dataKey="date"
                            tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#6b7280' }}
                            stroke={isDark ? '#475569' : '#d1d5db'}
                            tickFormatter={displayData.length > 365 ? formatDateFull : formatDate}
                            interval="preserveStartEnd"
                            minTickGap={40}
                            allowDataOverflow
                        />
                        <YAxis
                            yAxisId="left"
                            domain={[0, 'auto']}
                            tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#6b7280' }}
                            stroke={isDark ? '#475569' : '#d1d5db'}
                            tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                            label={{ value: 'Oil / Gas', angle: -90, position: 'insideLeft', fill: isDark ? '#64748b' : '#9ca3af', fontSize: 11 }}
                            allowDataOverflow
                        />
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            domain={[0, 'auto']}
                            tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#6b7280' }}
                            stroke={isDark ? '#475569' : '#d1d5db'}
                            tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                            label={{ value: showOilCut ? 'Water / Oil Cut' : 'Water (BBL)', angle: 90, position: 'insideRight', fill: isDark ? '#64748b' : '#9ca3af', fontSize: 11 }}
                            allowDataOverflow
                        />
                        {showOilCut && (
                            <YAxis
                                yAxisId="oilcut"
                                orientation="right"
                                domain={[0, 100]}
                                tick={{ fontSize: 11, fill: '#a855f7' }}
                                stroke="#a855f7"
                                tickFormatter={v => `${v}%`}
                                width={40}
                                allowDataOverflow
                            />
                        )}
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ color: isDark ? '#cbd5e1' : '#374151', fontSize: 12 }} />

                        {/* Oil highlight area fill */}
                        <Area yAxisId="left" type="monotone" dataKey="actual_oil" stroke="none" fill="#22c55e" fillOpacity={0.08} name=" " legendType="none" />

                        {/* Actual lines — oil is thickest */}
                        <Line yAxisId="left" type="monotone" dataKey="actual_oil" stroke="#22c55e" strokeWidth={3.5} dot={false} name="Oil (BBL)" connectNulls={false} />
                        <Line yAxisId="left" type="monotone" dataKey="actual_gas" stroke="#ef4444" strokeWidth={2} dot={false} name="Gas (MCF)" connectNulls={false} />
                        <Line yAxisId="right" type="monotone" dataKey="actual_water" stroke="#3b82f6" strokeWidth={2} dot={false} name="Water (BBL)" connectNulls={false} />

                        {/* Forecast lines - dashed */}
                        <Line yAxisId="left" type="monotone" dataKey="forecast_oil" stroke="#86efac" strokeWidth={2} strokeDasharray="8 4" dot={false} name="Oil Forecast" connectNulls />
                        <Line yAxisId="left" type="monotone" dataKey="forecast_gas" stroke="#fca5a5" strokeWidth={1.5} strokeDasharray="8 4" dot={false} name="Gas Forecast" connectNulls />
                        <Line yAxisId="right" type="monotone" dataKey="forecast_water" stroke="#93c5fd" strokeWidth={1.5} strokeDasharray="8 4" dot={false} name="Water Forecast" connectNulls />

                        {/* Oil Cut % line */}
                        {showOilCut && (
                            <Line yAxisId="oilcut" type="monotone" dataKey="oil_cut" stroke="#a855f7" strokeWidth={2} dot={false} name="Oil Cut %" connectNulls={false} />
                        )}

                        {/* Workover vertical markers — no labels, just subtle lines */}
                        {showWorkovers && visibleWorkovers.map(wo => (
                            <ReferenceLine
                                key={wo.jobId || `${wo.startDate}-${wo.wellName}`}
                                x={wo.startDate}
                                yAxisId="left"
                                stroke="#f59e0b"
                                strokeDasharray="4 4"
                                strokeWidth={1}
                                strokeOpacity={0.6}
                            />
                        ))}

                        {/* Drag selection highlight */}
                        {isDragging && refAreaLeft && refAreaRight && (
                            <ReferenceArea
                                yAxisId="left"
                                x1={refAreaLeft}
                                x2={refAreaRight}
                                strokeOpacity={0.3}
                                fill="#3b82f6"
                                fillOpacity={0.15}
                            />
                        )}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>

            {/* Workover events timeline below chart */}
            {visibleWorkovers.length > 0 && (
                <div className={`mt-3 rounded-lg border ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-amber-50/50 border-amber-200'}`}>
                    <div className={`px-3 py-1.5 border-b flex items-center justify-between ${isDark ? 'border-slate-700' : 'border-amber-200'}`}>
                        <span className={`text-xs font-semibold ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                            Workovers ({visibleWorkovers.length})
                        </span>
                    </div>
                    <div className="px-3 py-2 flex flex-wrap gap-1.5">
                        {visibleWorkovers.map((wo, i) => (
                            <span
                                key={wo.jobId || `${wo.startDate}-${wo.wellName}-${i}`}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] ${
                                    isDark ? 'bg-slate-700 text-gray-200' : 'bg-white text-gray-700 border border-amber-200'
                                }`}
                                title={`${wo.wellName} — ${wo.jobType}\n${formatDateFull(wo.startDate)}${wo.endDate ? ' to ' + formatDateFull(wo.endDate) : ''}`}
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0"></span>
                                <span className="font-semibold">{formatDate(wo.startDate)}</span>
                                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>|</span>
                                <span className="truncate max-w-[120px]">{wo.wellName}</span>
                                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>—</span>
                                <span className={`truncate max-w-[100px] ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>{wo.jobType}</span>
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

export default WellAnalysisChart
